import { useRef, useState, type CSSProperties } from 'react';
import { CalendarDays } from 'lucide-react';
import { enmascararFecha, toDDMMYYYY, toISO } from '@lib/bajas-revision';

/**
 * Estilo del campo dentro de una fila de tabla. Vivia en `pages/Bajas.tsx`
 * junto al resto de los estilos de esa pantalla y se mudo con el componente.
 */
const campoStyle: CSSProperties = {
  padding: '5px 8px',
  border: '1px solid var(--color-outline)',
  borderRadius: 8,
  fontSize: 12.5,
  maxWidth: 210,
};

/**
 * Campo de fecha en formato argentino, siempre dd/mm/aaaa.
 *
 * No usa `<input type="date">` a propósito: el control nativo se dibuja con el
 * formato del navegador/sistema, así que en una máquina en inglés el operador
 * ve mm/dd/aaaa y no hay atributo ni CSS que lo cambie. Acá se tipea sobre un
 * campo de texto con la barra puesta sola; el estado sigue guardándose en ISO
 * (`yyyy-mm-dd`) para poder comparar vigencia contra fallecimiento.
 *
 * Mientras la fecha esté incompleta o no exista (31/02), el valor que sube es
 * '' → la fila queda marcada para corregir y el lote no avanza.
 */
export function CampoFecha({
  value,
  onChange,
  ariaLabel,
}: {
  value: string;
  onChange: (iso: string) => void;
  /** Para usarlo fuera de un `<label>` (filtros de una barra, por ejemplo). */
  ariaLabel?: string;
}) {
  const [txt, setTxt] = useState(() => toDDMMYYYY(value));
  // El valor puede cambiar desde afuera (ej. se edita el motivo por lote y se
  // limpia la fecha). Se resincroniza solo cuando el ISO de arriba dejó de
  // coincidir con lo tipeado, para no pisar una fecha a medio escribir.
  const [ultimo, setUltimo] = useState(value);
  if (value !== ultimo) {
    setUltimo(value);
    if (value !== toISO(txt)) setTxt(toDDMMYYYY(value));
  }
  const incompleta = txt.length > 0 && !toISO(txt);
  const picker = useRef<HTMLInputElement>(null);

  function aplicarISO(iso: string) {
    setTxt(toDDMMYYYY(iso));
    setUltimo(iso);
    onChange(iso);
  }
  // El calendario nativo se sigue usando para ELEGIR (el input que lo dispara
  // está oculto); lo que no se usa es su forma de MOSTRAR la fecha, que depende
  // del idioma del navegador. `showPicker` es lo soportado hoy; el click es el
  // fallback para navegadores viejos y si el navegador lo rechaza no pasa nada.
  function abrirCalendario() {
    const el = picker.current;
    if (!el) return;
    try {
      if (typeof el.showPicker === 'function') el.showPicker();
      else el.click();
    } catch {
      el.click();
    }
  }

  return (
    <div style={{ position: 'relative' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <input
          type="text"
          inputMode="numeric"
          aria-label={ariaLabel}
          placeholder="dd/mm/aaaa"
          maxLength={10}
          value={txt}
          onChange={(e) => {
            const t = enmascararFecha(e.target.value);
            setTxt(t);
            setUltimo(toISO(t));
            onChange(toISO(t));
          }}
          style={{
            ...campoStyle,
            ...(incompleta ? { borderColor: '#b45309' } : {}),
          }}
        />
        <button
          type="button"
          onClick={abrirCalendario}
          title="Elegir del calendario"
          aria-label="Elegir del calendario"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            padding: 4,
            border: '1px solid var(--color-outline, #d8dee9)',
            borderRadius: 6,
            background: 'var(--color-surface-accent, #f8f8f8)',
            cursor: 'pointer',
            color: 'var(--color-element-mid)',
          }}
        >
          <CalendarDays size={14} />
        </button>
      </div>
      {/* Sin `display:none` ni `visibility:hidden`: el navegador ancla el popup
          del calendario a este input y necesita que esté renderizado. */}
      <input
        ref={picker}
        type="date"
        tabIndex={-1}
        aria-hidden
        value={value}
        onChange={(e) => aplicarISO(e.target.value)}
        style={{
          position: 'absolute',
          right: 0,
          bottom: 0,
          width: 1,
          height: 1,
          padding: 0,
          border: 0,
          opacity: 0,
          pointerEvents: 'none',
        }}
      />
      {incompleta && (
        <div style={{ fontSize: 11, color: '#b45309', marginTop: 2 }}>Fecha incompleta</div>
      )}
    </div>
  );
}
